import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_AI_API_URL || '/api/ai';

const MODELS = ['gpt-4o','gpt-4o-mini','gpt-4-turbo','gpt-4','gpt-3.5-turbo','o1-preview','o1-mini'];
const EMBEDDING_MODELS = ['text-embedding-3-small','text-embedding-3-large','text-embedding-ada-002'];

export default function AISettings() {
  const [form, setForm] = useState({ model: 'gpt-4o', embedding_model: 'text-embedding-3-small', temperature: 0.7, max_tokens: 1024, system_prompt: 'You are a helpful customer support assistant.', escalation_threshold: 0.4, escalation_keywords: ['human','agent','representative'], openai_api_key: '',
    whatsapp_enabled: false, whatsapp_phone_number_id: '',
    whatsapp_access_token: '', whatsapp_verify_token: '',
    has_whatsapp_token: false,
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [waTokenCopied, setWaTokenCopied] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/settings`, auth).then(r => {
      setForm(f => ({
        ...f, ...r.data,
        openai_api_key: '',
        whatsapp_access_token: '',
        has_whatsapp_token: r.data.has_whatsapp_token || false,
      }));
      setKeywordInput((r.data.escalation_keywords || []).join(', '));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setSaved(false);
    const keywords = keywordInput.split(',').map(k => k.trim()).filter(Boolean);
    const payload: Record<string, unknown> = { ...form, escalation_keywords: keywords };
    if (!payload.openai_api_key) delete payload.openai_api_key;
    if (!payload.whatsapp_access_token) delete payload.whatsapp_access_token;
    delete payload.has_whatsapp_token;
    try {
      await axios.put(`${API}/settings`, payload, auth);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };


  const generateVerifyToken = () => {
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    const tok = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
    setForm(f => ({ ...f, whatsapp_verify_token: tok }));
  };

  const copyText = (text: string, setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    navigator.clipboard.writeText(text).then(() => { setter(true); setTimeout(() => setter(false), 2000); });
  };

  const testConnection = async () => {
    setTesting(true); setTestResult('');
    try {
      const r = await axios.post(`${API}/chat`, { message: 'Hello, this is a connection test.', customer_id: 'test', channel: 'web_chat' }, auth);
      setTestResult(`✅ Connection OK — Model: ${r.data.model_used} · Confidence: ${(r.data.confidence_score * 100).toFixed(0)}%`);
    } catch (e: any) {
      setTestResult(`❌ Error: ${e.response?.data?.detail || e.message}`);
    } finally { setTesting(false); }
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({
      ...f,
      [k]: e.target.type === 'number' ? Number(e.target.value) : (e.target as HTMLInputElement).type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value,
    }));

  const webhookUrl = `${window.location.origin}/api/ai/webhook/whatsapp`;

  return (
    <div style={{ padding: 32, maxWidth: 760, margin: '0 auto', background: '#0f172a', minHeight: 'calc(100vh - 64px)', color: '#e2e8f0' }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>AI Settings</div>
      <div style={{ fontSize: 14, color: '#64748b', marginBottom: 28 }}>Configure the OpenAI integration, escalation behaviour, and response quality parameters.</div>

      <Section title="OpenAI API">
        <Field label="API Key (leave blank to use platform key)">
          <input type="password" placeholder="sk-…" value={form.openai_api_key} onChange={set('openai_api_key')} style={inputStyle} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Chat Model">
            <select value={form.model} onChange={set('model')} style={inputStyle}>
              {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
              <option value="custom">Custom model name…</option>
            </select>
          </Field>
          <Field label="Embedding Model">
            <select value={form.embedding_model} onChange={set('embedding_model')} style={inputStyle}>
              {EMBEDDING_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        </div>
        <button onClick={testConnection} disabled={testing} style={{ ...btnOutline, marginTop: 4 }}>
          {testing ? '🔄 Testing…' : '🔌 Test Connection'}
        </button>
        {testResult && <div style={{ marginTop: 8, fontSize: 13, color: testResult.startsWith('✅') ? '#10b981' : '#ef4444' }}>{testResult}</div>}
      </Section>

      <Section title="Response Quality">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label={`Temperature: ${form.temperature}`}>
            <input type="range" min={0} max={2} step={0.05} value={form.temperature} onChange={set('temperature')} style={{ width: '100%', accentColor: '#8b5cf6' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b' }}><span>Focused (0)</span><span>Creative (2)</span></div>
          </Field>
          <Field label="Max Tokens">
            <input type="number" min={64} max={32768} step={64} value={form.max_tokens} onChange={set('max_tokens')} style={inputStyle} />
          </Field>
        </div>
        <Field label="System Prompt">
          <textarea value={form.system_prompt} onChange={set('system_prompt')} rows={5}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 }} />
        </Field>
      </Section>

      <Section title="Escalation">
        <Field label={`Confidence Threshold: ${form.escalation_threshold}`}>
          <input type="range" min={0} max={1} step={0.05} value={form.escalation_threshold} onChange={set('escalation_threshold')} style={{ width: '100%', accentColor: '#f59e0b' }} />
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
            AI will escalate to agent when confidence drops below <strong style={{ color: '#f59e0b' }}>{(form.escalation_threshold * 100).toFixed(0)}%</strong>
          </div>
        </Field>
        <Field label="Escalation Keywords (comma separated)">
          <input value={keywordInput} onChange={e => setKeywordInput(e.target.value)} placeholder="human, agent, representative, speak to someone" style={inputStyle} />
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywordInput.split(',').filter(k => k.trim()).map(k => (
              <span key={k} style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>{k.trim()}</span>
            ))}
          </div>
        </Field>
      </Section>


      <Section title="WhatsApp Business API">
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4, lineHeight: 1.6 }}>
          Connect your Meta WhatsApp Business account so customers can message you on WhatsApp.
          WhatsApp conversations appear in the AI Inbox and are handled by the AI (or escalated to agents).
        </div>

        <Field label="">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
            <div
              onClick={() => setForm(f => ({ ...f, whatsapp_enabled: !f.whatsapp_enabled }))}
              style={{
                width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
                background: form.whatsapp_enabled ? '#25d366' : '#334155',
                transition: 'background 0.2s', cursor: 'pointer',
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: form.whatsapp_enabled ? 22 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s',
              }} />
            </div>
            <span style={{ fontSize: 13, color: form.whatsapp_enabled ? '#25d366' : '#64748b', fontWeight: 600 }}>
              {form.whatsapp_enabled ? 'WhatsApp enabled' : 'WhatsApp disabled'}
            </span>
          </label>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Phone Number ID">
            <input
              value={form.whatsapp_phone_number_id}
              onChange={set('whatsapp_phone_number_id')}
              placeholder="e.g. 123456789012345"
              style={inputStyle}
            />
          </Field>
          <Field label={`Access Token (${form.has_whatsapp_token ? '● already set — paste to replace' : 'not set'})`}>
            <input
              type="password"
              value={form.whatsapp_access_token}
              onChange={set('whatsapp_access_token')}
              placeholder={form.has_whatsapp_token ? '(unchanged)' : 'EAAxxxxxxxx…'}
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="Verify Token">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={form.whatsapp_verify_token}
              onChange={set('whatsapp_verify_token')}
              placeholder="Generate or enter your own"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={generateVerifyToken} style={{ ...btnOutline, whiteSpace: 'nowrap' }}>
              ⟳ Generate
            </button>
            {form.whatsapp_verify_token && (
              <button onClick={() => copyText(form.whatsapp_verify_token, setWaTokenCopied)} style={{ ...btnOutline, whiteSpace: 'nowrap' }}>
                {waTokenCopied ? '✓ Copied' : 'Copy'}
              </button>
            )}
          </div>
        </Field>

        <Field label="Webhook URL (paste this in Meta App Dashboard → WhatsApp → Webhooks)">
          <div style={{ display: 'flex', gap: 8 }}>
            <input readOnly value={webhookUrl} style={{ ...inputStyle, flex: 1, color: '#818cf8', cursor: 'text' }} />
            <button onClick={() => copyText(webhookUrl, setWebhookCopied)} style={{ ...btnOutline, whiteSpace: 'nowrap' }}>
              {webhookCopied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
            In Meta App Dashboard: <strong style={{ color: '#94a3b8' }}>WhatsApp → Configuration → Webhook</strong>.
            Subscribe to the <strong style={{ color: '#94a3b8' }}>messages</strong> field.
          </div>
        </Field>
      </Section>

      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        <button onClick={save} disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saved && <span style={{ alignSelf: 'center', color: '#10b981', fontSize: 13 }}>✅ Saved successfully</span>}
      </div>

      <EmbedCodeSection />
    </div>
  );
}

// ── Embed Code Generator ─────────────────────────────────────────────────────
function EmbedCodeSection() {
  const [kbs, setKbs] = React.useState<{id:string;name:string}[]>([]);
  const [kbId, setKbId] = React.useState('');
  const [title, setTitle] = React.useState('Support Chat');
  const [color, setColor] = React.useState('#8b5cf6');
  const [greeting, setGreeting] = React.useState('Hi! How can I help you today? 👋');
  const [copied, setCopied] = React.useState(false);
  const token = localStorage.getItem('token');

  React.useEffect(() => {
    axios.get('/api/ai/knowledge/bases', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setKbs(r.data || [])).catch(() => {});
  }, []);

  const [widgetHost, setWidgetHost] = React.useState('');
  const [tenantId, setTenantId] = React.useState('1');

  const wssHost = widgetHost.replace(/^https?:\/\//, '');
  const snippet = `<script\n  src="${widgetHost}/widget/chat-widget.js"\n  data-tenant-id="${tenantId}"\n  data-kb-id="${kbId}"\n  data-api-url="${widgetHost}/api/ai"\n  data-ws-url="wss://${wssHost}/api/ai"\n  data-title="${title}"\n  data-color="${color}"\n  data-greeting="${greeting}"\n  defer\n><\/script>`;

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{ background: '#1e293b', border: '1px solid #6366f144', borderRadius: 12, padding: 20, marginTop: 28 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#818cf8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        🌐 Website Embed Code
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
        Copy this snippet and paste it before <code style={{ background: '#0f172a', color: '#a78bfa', borderRadius: 4, padding: '1px 5px' }}>&lt;/body&gt;</code> on any webpage to add the chat widget.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Widget Domain</div>
          <input value={widgetHost} onChange={e => setWidgetHost(e.target.value)} style={inputStyle} placeholder="" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Tenant ID</div>
          <input value={tenantId} onChange={e => setTenantId(e.target.value)} style={inputStyle} placeholder="1" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Knowledge Base</div>
          <select value={kbId} onChange={e => setKbId(e.target.value)} style={inputStyle}>
            <option value="">— No knowledge base —</option>
            {kbs.map(kb => <option key={kb.id} value={kb.id}>{kb.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Widget Title</div>
          <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Brand Colour</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 40, height: 36, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 6 }} />
            <input value={color} onChange={e => setColor(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Greeting Message</div>
          <input value={greeting} onChange={e => setGreeting(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <pre style={{ background: '#0f172a', borderRadius: 8, padding: 16, fontSize: 12, color: '#e2e8f0', overflowX: 'auto', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {snippet}
        </pre>
        <button onClick={copy} style={{ position: 'absolute', top: 10, right: 10, background: copied ? '#10b981' : '#334155', border: 'none', color: 'white', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' }}>
          {copied ? '✓ Copied!' : 'Copy'}
        </button>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
        <a href="/widget/demo.html" target="_blank" style={{ fontSize: 13, color: '#818cf8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
          🔗 Open live demo page →
        </a>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#a78bfa', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = { width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, boxSizing: 'border-box' };
const btnPrimary: React.CSSProperties = { background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', border: 'none', color: '#fff', borderRadius: 10, padding: '11px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const btnOutline: React.CSSProperties = { background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' };
